import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { registerUser } from '../../services/accountService';
import {
  fetchInviteByToken,
  startInviteRegistration,
} from '../../services/inviteService';
import { getPlans } from '../../services/subscriptionService';
import './Register.css';
import Breadcrumbs from '../../components/Breadcrumbs';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import logo from '../../assets/icon.png';

const Register = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [vatCode, setvatCode] = useState('');
  const [registrationnumber, setregistrationnumber] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Invite-token gating state. When inviteToken is present we lock the email
  // field to the invited address and require the user to pick a paid plan.
  const [inviteState, setInviteState] = useState({
    loading: !!inviteToken,
    valid: false,
    blockerMessage: '',
  });
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');

  useEffect(() => {
    if (window.prerenderReady !== undefined) {
      window.prerenderReady = true;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!inviteToken) return;
      const res = await fetchInviteByToken(inviteToken);
      if (cancelled) return;
      if (!res.success) {
        setInviteState({
          loading: false,
          valid: false,
          blockerMessage: res.message || 'Invitatia nu mai este valabila.',
        });
        return;
      }
      setEmail(res.email || '');
      if (res.name) setName(res.name);
      if (res.phone) setPhone(res.phone);
      setInviteState({ loading: false, valid: true, blockerMessage: '' });

      try {
        const all = await getPlans();
        const active = (all || []).filter(
          (p) => p.enable !== false && p.status !== false,
        );
        setPlans(active);
        const monthly = active.find((p) => p.interval === 'month');
        const yearly = active.find((p) => p.interval === 'year');
        // Default to monthly so the user sees the cheaper plan first.
        const fallback = monthly || yearly || active[0];
        if (fallback) setSelectedPlanId(fallback.id);
      } catch (err) {
        // Plans failing to load is fatal for the paywalled flow — we cannot
        // start a checkout without a plan id.
        setInviteState({
          loading: false,
          valid: false,
          blockerMessage:
            'Nu am putut incarca planurile de abonament. Te rugam sa reincerci.',
        });
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('Parolele nu se potrivesc.');
      return;
    }

    // Paywalled flow: route through the invite/start-registration endpoint and
    // hand the user off to Stripe. The User row is created only after the Stripe
    // webhook confirms payment.
    if (inviteToken) {
      if (!inviteState.valid) {
        setError(inviteState.blockerMessage || 'Invitatia nu este valabila.');
        return;
      }
      if (!selectedPlanId) {
        setError('Te rugam sa selectezi un plan de abonament.');
        return;
      }
      setIsLoading(true);
      try {
        const res = await startInviteRegistration({
          InviteToken: inviteToken,
          Email: email,
          Name: name,
          Company: company,
          Phone: phone,
          Vatcode: vatCode,
          Registrationnumber: registrationnumber,
          Password: password,
          SubscriptionId: selectedPlanId,
          ClientUrl: window.location.origin,
        });
        if (res.success && res.sessionUrl) {
          setSuccess('Te redirectionam catre plata securizata Stripe…');
          window.location.assign(res.sessionUrl);
          return;
        }
        setError(res.message || 'Eroare la initierea platii.');
      } catch (err) {
        setError(err.message || 'O eroare neasteptata a aparut.');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Non-invite path: legacy direct registration (free / freemium account).
    // Existing users without an invite still rely on this flow.
    setIsLoading(true);
    try {
      const { success: registered, error: registerError } = await registerUser({
        email,
        password,
        name,
        company,
        vatCode,
        registrationnumber,
        phone,
      });

      if (registered) {
        setSuccess(
          'Cont creat cu succes! Te redirectionam catre alegerea abonamentului…',
        );
        setError('');
        setTimeout(() => navigate('/abonamente?newAccount=1'), 1500);
      } else {
        setError(registerError || 'Eroare la crearea contului.');
        setSuccess('');
      }
    } catch (err) {
      setError(err.message || 'O eroare neașteptată a apărut.');
      setSuccess('');
    } finally {
      setIsLoading(false);
    }
  };

  const breadcrumbLinks = [{ href: '/', label: 'Home' }, { label: 'Register' }];

  const pageTitle = `Înregistrare - ${import.meta.env.VITE_NAME} | Portal Achiziții Publice`;
  const pageDescription = `Creează un cont pe ${import.meta.env.VITE_NAME} pentru a accesa cele mai recente licitații publice și a beneficia de toate funcționalitățile platformei dedicate achizițiilor publice.`;
  const pageKeywords = `înregistrare, creare cont, achiziții publice, licitații publice, Licitatii-Constructii, acces platformă, creare utilizator`;
  const pageUrl = `${window.location.origin}/register`;

  if (inviteToken && inviteState.loading) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600">Verificam invitatia ta…</p>
        </div>
      </section>
    );
  }

  if (inviteToken && !inviteState.valid) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-3">
            Invitatie indisponibila
          </h2>
          <p className="text-gray-600 mb-6">{inviteState.blockerMessage}</p>
          <a
            href="/login"
            className="inline-block px-5 py-2 bg-indigo-500 text-white rounded-lg font-semibold hover:bg-indigo-600"
          >
            Mergi la autentificare
          </a>
        </div>
      </section>
    );
  }

  return (
    <>
      <SeoPageComponent
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        pageKeywords={pageKeywords}
        pageUrl={pageUrl}
        logoUrl={logo}
      />
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-green-100 py-12">
        <div className="w-full max-w-xl bg-white rounded-3xl shadow-2xl p-10 border-t-8 border-indigo-500 animate-fade-in">
          <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
          <h2 className="text-3xl md:text-4xl font-extrabold text-indigo-700 mb-2 text-center">
            Creează-ți contul și accesează oportunități reale!
          </h2>
          <p className="text-gray-500 text-center mb-8 text-lg">
            Alătură-te platformei Licitatii-Constructii și fii cu un pas înaintea
            concurenței în construcții.
          </p>
          {error && (
            <p className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 text-center">
              {error}
            </p>
          )}
          {success && (
            <p className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded mb-4 text-center">
              {success}
            </p>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                className="block text-gray-700 font-semibold mb-1"
                htmlFor="name"
              >
                Nume complet
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Introdu numele complet"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                required
              />
            </div>
            <div>
              <label
                className="block text-gray-700 font-semibold mb-1"
                htmlFor="email"
              >
                Adresă de email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Introdu adresa de email"
                className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none ${
                  inviteToken ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                required
                readOnly={!!inviteToken}
              />
              {inviteToken && (
                <p className="text-xs text-gray-500 mt-1">
                  Email-ul este blocat la adresa pentru care s-a emis invitatia.
                </p>
              )}
            </div>
            <div>
              <label
                className="block text-gray-700 font-semibold mb-1"
                htmlFor="company"
              >
                Companie
              </label>
              <input
                id="company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Introdu numele companiei"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                required
              />
            </div>
            <div>
              <label
                className="block text-gray-700 font-semibold mb-1"
                htmlFor="vatcode"
              >
                Cod Înregistrare
              </label>
              <input
                id="vatcode"
                type="text"
                value={vatCode}
                onChange={(e) => setvatCode(e.target.value)}
                placeholder="Ex. 33581368"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                required
              />
            </div>
            <div>
              <label
                className="block text-gray-700 font-semibold mb-1"
                htmlFor="registrationnumber"
              >
                Nr Înregistrare la Registrul Comerțului
              </label>
              <input
                id="registrationnumber"
                type="text"
                value={registrationnumber}
                onChange={(e) => setregistrationnumber(e.target.value)}
                placeholder="Ex: J40/244323/2025"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                required
              />
            </div>
            <div>
              <label
                className="block text-gray-700 font-semibold mb-1"
                htmlFor="phone"
              >
                Telefon
              </label>
              <input
                id="phone"
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Introdu numărul de telefon"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                required
              />
            </div>
            <div>
              <label
                className="block text-gray-700 font-semibold mb-1"
                htmlFor="password"
              >
                Parolă
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Introdu parola"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                required
              />
            </div>
            <div>
              <label
                className="block text-gray-700 font-semibold mb-1"
                htmlFor="confirm-password"
              >
                Confirmă Parola
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmă parola"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-400 outline-none"
                required
              />
            </div>

            {inviteToken && plans.length > 0 && (
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Alege planul de abonament
                </label>
                <div className="grid gap-3">
                  {plans.map((plan) => {
                    const isMonth = plan.interval === 'month';
                    const label = isMonth ? 'Lunar' : 'Anual';
                    return (
                      <label
                        key={plan.id}
                        className={`flex items-center justify-between border-2 rounded-xl px-4 py-3 cursor-pointer transition ${
                          selectedPlanId === plan.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-indigo-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="planSelection"
                            value={plan.id}
                            checked={selectedPlanId === plan.id}
                            onChange={() => setSelectedPlanId(plan.id)}
                            className="accent-indigo-500"
                          />
                          <div>
                            <p className="font-bold text-gray-800">
                              {plan.name || label}
                            </p>
                            <p className="text-xs text-gray-500">
                              {plan.credits ?? (isMonth ? 100 : 1500)} credite incluse
                            </p>
                          </div>
                        </div>
                        <p className="text-indigo-600 font-bold">
                          {plan.price} {(plan.currency || 'RON').toUpperCase()}
                        </p>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || (inviteToken && !selectedPlanId)}
              className="w-full bg-gradient-to-r from-indigo-500 to-green-500 hover:from-indigo-600 hover:to-green-600 disabled:from-gray-400 disabled:to-gray-400 text-white font-bold py-3 rounded-xl shadow-lg text-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  Se proceseaza…
                </>
              ) : inviteToken ? (
                'Continua catre plata'
              ) : (
                'Creeaza cont'
              )}
            </button>
          </form>
          <div className="flex items-center my-6">
            <span className="flex-1 h-px bg-gray-200"></span>
            <span className="mx-4 text-gray-400 font-semibold">sau</span>
            <span className="flex-1 h-px bg-gray-200"></span>
          </div>
          <div className="text-center">
            <p className="text-gray-600">
              Ai deja un cont?{' '}
              <a
                href="/login"
                className="text-indigo-600 font-bold hover:underline"
              >
                Autentifică-te
              </a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Register;
